import React, { useEffect, useState } from 'react';
import { Mail, Loader2, Send, ShieldCheck, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import { easerPost, errorMessage, isUnauthorized } from '../../../lib/easerApi';
import { eur } from './devisFormat';

/**
 * Envoi du devis au client par e-mail (messagerie de l'agence, réponse adressée au manager).
 * Un devis brouillon est d'abord validé dans Dolibarr pour obtenir sa référence définitive.
 */
export function DevisSendDialog({ quote, onClose, onSent, onUnauthorized }) {
  const [to, setTo] = useState('');
  const [ccAgency, setCcAgency] = useState(true);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (quote) { setTo(quote.client?.email || ''); setMessage(''); setCcAgency(true); }
  }, [quote]);

  if (!quote) return null;
  const isDraft = Number(quote.dolibarr?.status) === 0;
  const alreadySent = quote.email?.at;

  const send = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())) return toast.error('Indiquez une adresse e-mail valide');
    setBusy(true);
    try {
      const r = await easerPost(`/api/easer/devis-green/quotes/${quote.id}/send`, { to: to.trim(), cc_agency: ccAgency, message });
      toast.success(`Devis ${r.data.quote.dolibarr.ref} envoyé à ${r.data.sent.to}`);
      onSent?.(r.data.quote);
      onClose();
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, 'Envoi du devis impossible'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!quote} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="devis-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[hsl(var(--primary))]" /> Envoyer le devis au client</DialogTitle>
          <DialogDescription>
            {quote.client?.name} · {quote.dolibarr?.ref} · {eur(quote.dolibarr?.total_ttc ?? quote.computed?.totals?.ttc)} TTC
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3">
          {isDraft && (
            <p className="text-xs rounded-lg bg-[hsl(var(--brand-apricot))]/15 p-2.5 flex items-start gap-2" data-testid="devis-send-validate-notice">
              <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Le devis sera d'abord <strong>validé dans Dolibarr</strong> (référence définitive, plus de « PROV »). Il ne sera plus modifiable : vérifiez le PDF avant d'envoyer.</span>
            </p>
          )}
          {alreadySent && (
            <p className="text-xs text-muted-foreground flex items-start gap-2" data-testid="devis-send-already">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Déjà envoyé à {quote.email.to} le {new Date(quote.email.at).toLocaleDateString('fr-FR')}. Un nouvel envoi est possible.
            </p>
          )}
          <div>
            <Label htmlFor="send-to">E-mail du client</Label>
            <Input id="send-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@exemple.fr" required className="h-11" autoFocus={!to} data-testid="devis-send-to" />
            {!quote.client?.email && <p className="text-[11px] text-muted-foreground mt-1">Aucune adresse dans Dolibarr pour ce client : saisissez-la.</p>}
          </div>
          <div>
            <Label htmlFor="send-message">Message personnel (facultatif)</Label>
            <Textarea id="send-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1500} placeholder="Ex. : Suite à notre visite de ce matin…" data-testid="devis-send-message" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={ccAgency} onCheckedChange={(v) => setCcAgency(!!v)} data-testid="devis-send-cc" /> Envoyer une copie à l'agence
          </label>
          <p className="text-[11px] text-muted-foreground">Envoyé depuis la messagerie de l'agence ; les réponses du client vous parviennent directement (votre adresse Dolibarr).</p>
          <Button type="submit" disabled={busy} className="w-full h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="devis-send-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} {isDraft ? 'Valider et envoyer le PDF' : 'Envoyer le PDF'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
